import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  SparklesIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import '../styles/scrollbar.css';
import { aiService, type GeneratedVideo } from '../services/aiService';

// Components
const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/generation"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-orange-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Video Generation</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          title={showHistory ? 'Hide History' : 'Show History'}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const VideoPromptForm: React.FC<{
  prompt: string;
  loading: boolean;
  onPromptChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}> = ({ prompt, loading, onPromptChange, onSubmit }) => (
  <form onSubmit={onSubmit} className="mb-8">
    <div className="flex flex-col space-y-4">
      <div className="relative">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe the video you want to generate..."
          className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-white placeholder-gray-400"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !prompt.trim()}
          className={`absolute bottom-3 right-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
            loading || !prompt.trim()
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:from-orange-700 hover:to-red-700 shadow-lg hover:shadow-orange-500/25'
          }`}
        >
          {loading ? (
            <>
              <SparklesIcon className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <VideoCameraIcon className="w-5 h-5" />
              <span>Generate</span>
            </>
          )}
        </button>
      </div>
    </div>
  </form>
);

const VideoCard: React.FC<{
  video: GeneratedVideo;
  onDelete: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}> = ({ video, onDelete, onDownload, isDownloading }) => (
  <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-transform hover:scale-[1.02] hover:shadow-xl group">
    <div className="aspect-video relative">
      <video
        src={video.url}
        controls
        className="w-full h-full object-cover"
        preload="metadata"
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
        <button
          onClick={onDownload}
          className={`p-2 bg-blue-500/20 hover:bg-blue-500/40 rounded-full text-blue-400 hover:text-blue-300 transition-colors ${
            isDownloading ? 'animate-pulse' : ''
          }`}
          title="Download video"
          disabled={isDownloading}
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full text-red-400 hover:text-red-300 transition-colors"
          title="Delete video"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
    <div className="p-4 space-y-2">
      <p className="text-sm text-gray-300 line-clamp-2">{video.prompt}</p>
      <p className="text-xs text-gray-500">{formatDate(video.timestamp)}</p>
    </div>
  </div>
);

const HistorySidebar: React.FC<{
  show: boolean;
  videos: GeneratedVideo[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onDownload: (url: string, index: number) => void;
  downloading: number | null;
}> = ({ show, videos, onClose, onDelete, onDownload, downloading }) => (
  <div
    className={`fixed right-0 top-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
      show ? 'translate-x-0' : 'translate-x-full'
    } z-50`}
  >
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-orange-400" />
            History
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">{videos.length} videos</span>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {videos.length > 0 ? (
          videos.map((video, index) => (
            <div
              key={index}
              className="group relative bg-gray-700/50 rounded-lg p-3 hover:bg-gray-700/70 transition-colors"
            >
              <div className="aspect-video w-full rounded-lg overflow-hidden mb-3">
                <video
                  src={video.url}
                  className="w-full h-full object-cover"
                  preload="metadata"
                />
              </div>
              <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                {video.prompt}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {formatDate(video.timestamp)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onDownload(video.url, index)}
                    className={`p-1.5 rounded-full text-gray-400 hover:text-blue-400 transition-colors ${
                      downloading === index ? 'animate-pulse text-blue-400' : ''
                    }`}
                    disabled={downloading === index}
                    title="Download video"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(index)}
                    className="p-1.5 rounded-full text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete video"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/50 mb-3">
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-400 text-sm">No history yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Generated videos will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 mb-4">
      <VideoCameraIcon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-300 mb-2">No videos generated yet</h3>
    <p className="text-gray-500">Start by describing the video you want to create</p>
  </div>
);

// Utility Functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Main Component
const VideoGeneration: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const { setLoading: setPageLoading } = useLoading();

  useEffect(() => {
    const initializePage = async () => {
      setPageLoading(true);
      try {
        const savedVideos = localStorage.getItem('generatedVideos');
        if (savedVideos) {
          const parsedVideos = JSON.parse(savedVideos).map((video: any) => ({
            ...video,
            timestamp: new Date(video.timestamp)
          }));
          setVideos(parsedVideos);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Error loading saved videos:', error);
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
  }, [setPageLoading]);

  useEffect(() => {
    localStorage.setItem('generatedVideos', JSON.stringify(videos));
  }, [videos]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setLoading(true);

    try {
      const generatedVideo = await aiService.generateVideo(prompt);
      setVideos(prev => [generatedVideo, ...prev]);
      setPrompt('');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url: string, index: number) => {
    try {
      setDownloading(index);
      const blob = await aiService.downloadMedia(url);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `generated-video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading video:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteVideo = (index: number) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header
        showHistory={showHistory}
        onToggleHistory={() => setShowHistory(!showHistory)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        {/* Page Title and Description */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">AI Video Generation</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Transform your ideas into captivating videos using our advanced AI model.
            Simply describe what you want to see, and watch your vision come to life.
          </p>
        </div>

        {/* Video Generation Form */}
        <div className="max-w-3xl mx-auto mb-12">
          <VideoPromptForm
            prompt={prompt}
            loading={loading}
            onPromptChange={setPrompt}
            onSubmit={handleSubmit}
          />
        </div>

        {/* Generated Videos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8">
          {videos.map((video, index) => (
            <VideoCard
              key={index}
              video={video}
              onDelete={() => handleDeleteVideo(index)}
              onDownload={() => handleDownload(video.url, index)}
              isDownloading={downloading === index}
            />
          ))}
        </div>

        {videos.length === 0 && !loading && <EmptyState />}
      </div>

      <HistorySidebar
        show={showHistory}
        videos={videos}
        onClose={() => setShowHistory(false)}
        onDelete={handleDeleteVideo}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </div>
  );
};

export default VideoGeneration;
